import type { LocationQuery } from 'vue-router'

/**
 * Derive the SEO page number from a route `page` query value.
 *
 * Sourced from the URL rather than from client-fetched pagination state, so the
 * canonical / hreflang markup computed from it is identical for every
 * user-agent during SSR (ECOM-2635 — the crawler path fetches products on the
 * server while the human path fetches them client-side, so pagination state is
 * UA-dependent during SSR; the URL is not).
 *
 * Page 1 collapses to the bare, indexable path; only pages >= 2 carry `?page=N`,
 * mirroring `paginationLinkTo()` in `useRouteHelpers`. Non-integer, non-positive,
 * missing, or malformed values (including the array form of a repeated query
 * key) resolve to page 1 — the safe, indexable default. This intentionally
 * differs from the `parseInt`-based parser at `ProductList.vue` (which would
 * accept `2.5` as `2`); a non-integer here yields page 1 on purpose.
 */
export function parsePageParam(
  page: LocationQuery[string] | undefined,
): number {
  const raw = Array.isArray(page) ? page[0] : page
  const parsed = Number(raw)
  return Number.isInteger(parsed) && parsed > 1 ? parsed : 1
}
